/**
 * Vincular um débito do extrato a um fornecedor: escolhe o fornecedor, vê os
 * lançamentos existentes dele na conta e as parcelas em aberto dos pedidos, e
 * marca o que explica o valor — a soma tem de fechar ao centavo (o backend
 * confere de novo). Lançamento existente é MANTIDO (só vinculado; a data
 * acompanha a do banco); parcela em aberto vira um lançamento novo por parcela.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link2, Sparkles } from 'lucide-react';
import bankIntegrationService from '@services/bankIntegrationService';
import type { Provider } from '@/types/provider';
import type {
  BankStatementDecisionItem,
  BankStatementPreviewItem,
  BankStatementSupplierCandidates,
  SupplierPaymentCandidate,
  SupplierPaymentSuggestion,
} from '@/types/bankIntegration';
import { formatCurrencyBRL, formatDateBR, type CategoryOptionGroup } from './financialUtils';
import { CategorySelect, ModalShell, labelCls, primaryBtnCls, secondaryBtnCls, selectCls } from './financialShared';
import {
  buildSupplierDecision,
  candidateKey,
  candidateLabel,
  selectionHasInstallments,
  selectionMatches,
  selectionTotalCents,
  shouldLearnByDefault,
  suggestionKindLabel,
} from './bankStatementSupplier';

interface Props {
  accountId: number;
  item: BankStatementPreviewItem;
  providers: Provider[];
  /** Categorias de Saída — usadas só para os lançamentos criados de parcelas. */
  categoryGroups: CategoryOptionGroup[];
  initialProviderId?: number | null;
  /** Sugestão aceita na tabela: já entra selecionada. */
  initialSelection?: SupplierPaymentSuggestion | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (decision: BankStatementDecisionItem) => void;
}

const formatCents = (cents: number): string => formatCurrencyBRL(cents / 100);

export const SupplierPaymentLinkModal: React.FC<Props> = ({
  accountId,
  item,
  providers,
  categoryGroups,
  initialProviderId,
  initialSelection,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [providerId, setProviderId] = useState<number | null>(initialProviderId ?? item.supplier?.providerId ?? null);
  const [data, setData] = useState<BankStatementSupplierCandidates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((initialSelection?.items ?? []).map(candidateKey)),
  );
  const [categoryErpId, setCategoryErpId] = useState<number | null>(null);
  const [learn, setLearn] = useState<boolean>(() => shouldLearnByDefault(item));

  // Candidatos do fornecedor escolhido (lançamentos da conta + parcelas em aberto).
  useEffect(() => {
    let active = true;
    if (providerId == null) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    bankIntegrationService
      .getSupplierCandidates(accountId, providerId, item.date, item.amountCents)
      .then(res => {
        if (active) setData(res);
      })
      .catch(err => {
        if (active) {
          setData(null);
          setError(err instanceof Error ? err.message : 'Erro ao carregar os candidatos do fornecedor');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accountId, providerId, item.date, item.amountCents]);

  const candidates = useMemo(() => data?.candidates ?? [], [data]);
  const transactions = useMemo(() => candidates.filter(c => c.kind === 'lancamento'), [candidates]);
  const installments = useMemo(() => candidates.filter(c => c.kind === 'parcela'), [candidates]);
  const totalCents = selectionTotalCents(candidates, selected);
  const matches = selectionMatches(totalCents, item.amountCents);
  const hasInstallments = selectionHasInstallments(candidates, selected);
  const remaining = Math.abs(item.amountCents) - totalCents;

  const toggle = (c: SupplierPaymentCandidate) => {
    const key = candidateKey(c);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const applySuggestion = (s: SupplierPaymentSuggestion) => {
    setSelected(new Set(s.items.map(candidateKey)));
  };

  const submit = () => {
    if (providerId == null || !matches) return;
    const chosen = candidates.filter(c => selected.has(candidateKey(c)));
    onSubmit(buildSupplierDecision(item, providerId, chosen, categoryErpId, learn));
  };

  const CandidateRow: React.FC<{ c: SupplierPaymentCandidate }> = ({ c }) => {
    const checked = selected.has(candidateKey(c));
    const moves = c.kind === 'lancamento' && c.date !== item.date;
    return (
      <label
        className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs cursor-pointer ${
          checked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'
        }`}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          checked={checked}
          onChange={() => toggle(c)}
        />
        <span className="min-w-0 flex-1">
          <span className="font-medium text-slate-800">{candidateLabel(c)}</span>
          <span className="block text-slate-500 truncate" title={c.description}>
            {c.description}
          </span>
          <span className="block text-[11px] text-slate-400">
            {c.kind === 'parcela' ? 'vence ' : ''}
            {formatDateBR(c.date)}
            {c.originalDate && ` (pedido: ${formatDateBR(c.originalDate)})`}
            {moves && <span className="text-amber-600"> · data será movida para {formatDateBR(item.date)}</span>}
          </span>
        </span>
        <span className="font-medium text-slate-800 whitespace-nowrap">{formatCents(c.amountCents)}</span>
      </label>
    );
  };

  return (
    <ModalShell
      title={`Vincular a fornecedor · ${formatCents(Math.abs(item.amountCents))}`}
      icon={<Link2 size={18} className="text-emerald-600" />}
      onClose={onClose}
      maxWidth="max-w-3xl"
      footer={
        <>
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className={primaryBtnCls}
            disabled={submitting || providerId == null || !matches}
            title={!matches ? 'A soma dos itens precisa fechar o valor do evento' : undefined}
            onClick={submit}
          >
            {submitting ? 'Vinculando...' : 'Vincular'}
          </button>
        </>
      }
    >
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
        <p className="font-medium text-slate-800 break-words">{item.description}</p>
        <p className="text-slate-500 text-xs mt-1">
          {formatDateBR(item.date)} · {formatCents(item.amountCents)}
          {item.counterpart && ` · ${item.counterpart}`}
          {item.counterpartDocument && ` (${item.counterpartDocument})`}
        </p>
      </div>

      <div>
        <label className={labelCls}>Fornecedor *</label>
        <select
          className={selectCls}
          value={providerId ?? ''}
          onChange={e => {
            setProviderId(e.target.value === '' ? null : Number(e.target.value));
            setSelected(new Set());
          }}
        >
          <option value="">— Selecione —</option>
          {providers.map(p => (
            <option key={p.provider_id} value={p.provider_id}>
              {p.trade_name || p.legal_name}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-slate-500">Carregando lançamentos e parcelas do fornecedor...</p>}

      {data && !loading && (
        <>
          {data.suggestions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Combinações que fecham o valor</p>
              <div className="flex flex-col gap-1">
                {data.suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => applySuggestion(s)}
                    className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-left text-xs text-emerald-800 hover:bg-emerald-100"
                  >
                    <Sparkles size={12} className="shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{suggestionKindLabel(s)}</span>
                      {' · '}
                      {s.items.map(i => `${candidateLabel(i)} ${formatCents(i.amountCents)}`).join(' + ')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Lançamentos existentes na conta ({transactions.length})</p>
              {transactions.length === 0 ? (
                <p className="text-xs text-slate-400">Nenhum lançamento deste fornecedor nesta conta sem vínculo.</p>
              ) : (
                <div className="flex flex-col gap-1 max-h-72 overflow-y-auto pr-1">
                  {transactions.map(c => (
                    <CandidateRow key={candidateKey(c)} c={c} />
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Parcelas em aberto dos pedidos ({installments.length})</p>
              {installments.length === 0 ? (
                <p className="text-xs text-slate-400">Nenhuma parcela sem lançamento nos pedidos deste fornecedor.</p>
              ) : (
                <div className="flex flex-col gap-1 max-h-72 overflow-y-auto pr-1">
                  {installments.map(c => (
                    <CandidateRow key={candidateKey(c)} c={c} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className={`rounded-md border px-3 py-2 text-sm ${
              matches ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-700'
            }`}
          >
            Selecionado: <strong>{formatCents(totalCents)}</strong> de {formatCents(Math.abs(item.amountCents))}
            {!matches && totalCents > 0 && (
              <span className="text-amber-700">
                {' '}
                · {remaining > 0 ? `faltam ${formatCents(remaining)}` : `${formatCents(-remaining)} a mais`}
              </span>
            )}
            {matches && ' · fecha ao centavo'}
          </div>

          {hasInstallments && (
            <div>
              <label className={labelCls}>Categoria dos lançamentos criados das parcelas</label>
              <CategorySelect
                groups={categoryGroups}
                value={categoryErpId}
                onChange={setCategoryErpId}
                emptyLabel="— categoria do pedido de compra —"
              />
              <p className="text-xs text-slate-400 mt-1">
                Cada parcela marcada vira um lançamento na data do extrato, já com fornecedor e pedido.
              </p>
            </div>
          )}

          {item.counterpartDocument && (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={learn}
                onChange={e => setLearn(e.target.checked)}
              />
              Lembrar a contraparte {item.counterpartDocument} para este fornecedor (próximos eventos são identificados sozinhos)
            </label>
          )}
        </>
      )}
    </ModalShell>
  );
};

export default SupplierPaymentLinkModal;
